<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  originalName: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['continue', 'cancel']);

const { t } = useI18n();

const selectedOption = ref('same_name');
const newTemplateName = ref('');
const nameError = ref('');

const isValid = computed(() => {
  if (selectedOption.value === 'same_name') {
    return true;
  }
  return (
    selectedOption.value === 'new_name' &&
    newTemplateName.value.trim() !== '' &&
    !nameError.value
  );
});

const validateName = () => {
  const name = newTemplateName.value.trim();

  if (!name) {
    nameError.value = t('SETTINGS.TEMPLATES.UPDATE_MODAL.NAME_REQUIRED');
    return;
  }

  if (name === props.originalName) {
    nameError.value = t(
      'SETTINGS.TEMPLATES.UPDATE_MODAL.NAME_MUST_BE_DIFFERENT'
    );
    return;
  }

  // WhatsApp template name requirements
  if (!/^[a-z0-9_]+$/.test(name)) {
    nameError.value = t('SETTINGS.TEMPLATES.UPDATE_MODAL.NAME_INVALID_FORMAT');
    return;
  }

  if (name.length < 1 || name.length > 512) {
    nameError.value = t('SETTINGS.TEMPLATES.UPDATE_MODAL.NAME_LENGTH_ERROR');
    return;
  }

  nameError.value = '';
};

const onContinue = () => {
  if (!isValid.value) return;

  emit('continue', {
    option: selectedOption.value,
    newName:
      selectedOption.value === 'new_name' ? newTemplateName.value.trim() : null,
  });
};

const onCancel = () => {
  emit('cancel');
};

// Reset when modal is shown
watch(
  () => props.show,
  show => {
    if (show) {
      selectedOption.value = 'same_name';
      newTemplateName.value = '';
      nameError.value = '';
    }
  }
);
</script>

<template>
  <Modal :show="show" :on-close="onCancel">
    <div class="flex flex-col gap-4 p-6">
      <!-- Header -->
      <div class="flex items-start gap-3">
        <div
          class="flex-shrink-0 w-12 h-12 rounded-full bg-n-amber-2 flex items-center justify-center"
        >
          <span class="i-lucide-alert-triangle size-6 text-n-amber-9" />
        </div>
        <div class="flex-1">
          <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
            {{ $t('SETTINGS.TEMPLATES.UPDATE_MODAL.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ $t('SETTINGS.TEMPLATES.UPDATE_MODAL.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Warning Box -->
      <div class="bg-n-amber-2 border border-n-amber-5 rounded-lg p-4">
        <div class="flex items-start gap-2">
          <span
            class="i-lucide-info size-5 text-n-amber-9 flex-shrink-0 mt-0.5"
          />
          <div class="flex-1 text-sm text-n-slate-12">
            <p class="font-medium mb-2">
              {{ $t('SETTINGS.TEMPLATES.UPDATE_MODAL.WARNING_TITLE') }}
            </p>
            <ul class="list-disc pl-5 space-y-1 text-n-slate-11">
              <li>
                {{ $t('SETTINGS.TEMPLATES.UPDATE_MODAL.WARNING_POINT_1') }}
              </li>
              <li>
                {{ $t('SETTINGS.TEMPLATES.UPDATE_MODAL.WARNING_POINT_2') }}
              </li>
              <li>
                {{ $t('SETTINGS.TEMPLATES.UPDATE_MODAL.WARNING_POINT_3') }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Options -->
      <div class="space-y-3">
        <p class="text-sm font-medium text-n-slate-12">
          {{ $t('SETTINGS.TEMPLATES.UPDATE_MODAL.CHOOSE_OPTION') }}
        </p>

        <!-- Option 1: Keep Same Name -->
        <label
          class="flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors"
          :class="
            selectedOption === 'same_name'
              ? 'border-n-blue-9 bg-n-blue-2'
              : 'border-n-weak hover:border-n-blue-5'
          "
        >
          <input
            v-model="selectedOption"
            type="radio"
            name="update-option"
            value="same_name"
            class="mt-0.5"
          />
          <div class="flex-1">
            <p class="font-medium text-n-slate-12 mb-1">
              {{ $t('SETTINGS.TEMPLATES.UPDATE_MODAL.OPTION_SAME_NAME') }}
            </p>
            <p class="text-sm text-n-slate-11">
              {{
                $t(
                  'SETTINGS.TEMPLATES.UPDATE_MODAL.OPTION_SAME_NAME_DESCRIPTION'
                )
              }}
            </p>
          </div>
        </label>

        <!-- Option 2: New Name -->
        <label
          class="flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors"
          :class="
            selectedOption === 'new_name'
              ? 'border-n-blue-9 bg-n-blue-2'
              : 'border-n-weak hover:border-n-blue-5'
          "
        >
          <input
            v-model="selectedOption"
            type="radio"
            name="update-option"
            value="new_name"
            class="mt-0.5"
          />
          <div class="flex-1">
            <p class="font-medium text-n-slate-12 mb-1">
              {{ $t('SETTINGS.TEMPLATES.UPDATE_MODAL.OPTION_NEW_NAME') }}
            </p>
            <p class="text-sm text-n-slate-11 mb-3">
              {{
                $t(
                  'SETTINGS.TEMPLATES.UPDATE_MODAL.OPTION_NEW_NAME_DESCRIPTION'
                )
              }}
            </p>

            <!-- New Name Input (shown when this option is selected) -->
            <div v-if="selectedOption === 'new_name'" class="mt-3">
              <Input
                v-model="newTemplateName"
                :placeholder="
                  $t('SETTINGS.TEMPLATES.UPDATE_MODAL.NEW_NAME_PLACEHOLDER')
                "
                :error="nameError"
                @input="validateName"
              />
              <p v-if="nameError" class="text-xs text-n-ruby-9 mt-1">
                {{ nameError }}
              </p>
              <p v-else class="text-xs text-n-slate-11 mt-1">
                {{
                  $t('SETTINGS.TEMPLATES.UPDATE_MODAL.NEW_NAME_HINT', {
                    original: originalName,
                  })
                }}
              </p>
            </div>
          </div>
        </label>
      </div>

      <!-- Actions -->
      <div
        class="flex items-center justify-end gap-3 pt-4 border-t border-n-weak"
      >
        <NextButton
          slate
          faded
          :label="$t('GENERAL.CANCEL')"
          @click="onCancel"
        />
        <NextButton
          blue
          :label="$t('SETTINGS.TEMPLATES.UPDATE_MODAL.CONTINUE')"
          :disabled="!isValid"
          @click="onContinue"
        />
      </div>
    </div>
  </Modal>
</template>
